import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { getReminderMessage } from '../../../../api/collections';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import ReminderMessagePreview from './ReminderMessagePreview';
import {
  CHANNEL_LABELS,
  apiErrorCode,
  collectionsErrorText,
  formatInstant,
} from '../utils/collectionReminders';
import { messageTemplateName, requestedTemplatesOf } from '../utils/reminderTemplates';

/**
 * Texto guardado de un mensaje (GET messages/:messageRef). Se pide al abrir: el historial
 * no trae los textos. Solo la última carga escribe (un "Reintentar" no se pisa con una
 * respuesta vieja) y nada escribe tras cerrar.
 */
function useReminderMessage(messageRef) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState({ loading: true, data: null, error: null });
    try {
      const { data } = await getReminderMessage(messageRef);
      if (seq !== requestSeq.current) return;
      setState({ loading: false, data, error: null });
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setState({ loading: false, data: null, error: err });
    }
  }, [messageRef]);

  useEffect(() => {
    load();
    return () => {
      requestSeq.current += 1;
    };
  }, [load]);

  return { ...state, reload: load };
}

/** `contentStatus` de billing: el texto es el del último intento, cuyo resultado no se sabe. */
const UNCERTAIN = 'uncertain';

/**
 * "Ver mensaje" del historial: el texto de una fila (burbuja de WhatsApp o correo aislado),
 * con su plantilla y cuántas notas incluía. Se monta al abrirlo; la fila lo ofrece solo si
 * billing dice que tiene el texto (`messageAvailable`).
 *
 * Billing guarda el texto que SALIÓ (`contentStatus: 'delivered'`) o, si el último intento
 * terminó sin saberse si se entregó, el de ese intento (`'uncertain'`): así se rotula, y
 * nunca se dice "Salió con" de un texto que pudo no salir.
 *
 * Un mensaje agrupado junta envíos que pedían plantillas distintas y sale con una sola:
 * se muestra con cuál salió y cuáles se pedían. Si billing no tiene el texto responde 404
 * `message_not_found` (anterior a las plantillas, vencido o nunca armado): se dice sin
 * suponer el motivo.
 *
 * Los nombres de plantilla y del comercio son datos: van fuera de t().
 *
 * @param {{ dispatch: { messageRef: string, channel: string, note?: { companyName?: string } },
 *   onClose: () => void, timeZone?: string }} props
 */
export default function ReminderMessageModal({ dispatch, onClose, timeZone }) {
  const { t } = useTranslation();
  const { loading, data, error, reload } = useReminderMessage(dispatch.messageRef);
  const channel = data?.channel ?? dispatch.channel;
  const description = [t(CHANNEL_LABELS[channel] ?? channel), dispatch.note?.companyName]
    .filter(Boolean)
    .join(' · ');

  let content;
  if (loading) {
    content = (
      <div className="space-y-3" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  } else if (error && apiErrorCode(error) === 'message_not_found') {
    content = (
      <p className="m-0 rounded-md border border-border bg-bg-subtle px-4 py-3 text-sm text-fg">
        {t('El texto de este envío no está disponible.')}
      </p>
    );
  } else if (error) {
    content = (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>{collectionsErrorText(error, t, 'No se pudo cargar el mensaje.')}</span>
        <Button size="sm" variant="outline" onClick={reload}>
          {t('Reintentar')}
        </Button>
      </div>
    );
  } else if (data) {
    const requested = requestedTemplatesOf(data);
    const grouped = requested.length > 0;
    const version = data.template?.version;
    const notes = Array.isArray(data.noteIds) ? data.noteIds.length : 0;
    const renderedAt = formatInstant(data.renderedAt, timeZone);
    const uncertain = data.contentStatus === UNCERTAIN;
    let notesText;
    if (uncertain) {
      notesText = notes === 1 ? t('Este texto incluía 1 nota.') : t('Este texto incluía {{n}} notas.', { n: notes });
    } else {
      notesText = notes === 1 ? t('Este mensaje incluyó 1 nota.') : t('Este mensaje incluyó {{n}} notas.', { n: notes });
    }
    content = (
      <div className="space-y-3">
        {uncertain && (
          <p className="m-0 flex items-start gap-1.5 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{t('Texto del último intento (resultado incierto)')}</span>
          </p>
        )}
        <dl className="m-0 grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-fg-muted">{uncertain ? t('Plantilla') : t('Salió con')}</dt>
          <dd className="m-0 flex flex-wrap items-center gap-2">
            <span className="break-words font-medium text-fg">{messageTemplateName(data.template, t)}</span>
            {Number.isInteger(version) && (
              <span className="text-xs text-fg-muted">{t('Versión {{n}}', { n: version })}</span>
            )}
            {grouped && <Badge tone="info">{t('Agrupado')}</Badge>}
          </dd>
          {grouped && (
            <>
              <dt className="text-fg-muted">{t('Plantillas pedidas')}</dt>
              <dd className="m-0 break-words text-fg">
                {requested.map((template) => messageTemplateName(template, t)).join(', ')}
              </dd>
            </>
          )}
          {renderedAt && (
            <>
              <dt className="text-fg-muted">{t('Armado el')}</dt>
              <dd className="m-0 text-fg">{renderedAt}</dd>
            </>
          )}
        </dl>
        {grouped && (
          <p className="m-0 text-xs text-fg-muted">
            {t('Se juntaron en un solo mensaje envíos al mismo comercio que pedían plantillas distintas: salió con una sola.')}
          </p>
        )}
        <p className="m-0 text-sm text-fg">{notesText}</p>
        <ReminderMessagePreview channel={data.channel} subject={data.subject} html={data.html} text={data.text} />
      </div>
    );
  }

  return (
    <Modal open onClose={onClose} size="lg" title={t('Texto del mensaje')} description={description}>
      {content}
    </Modal>
  );
}
